import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WarehouseItem, useUpdateWarehouseItem } from '@/hooks/warehouse';
import { useOrganizations } from '@/hooks/useOrganizations';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

interface EditWarehouseItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouseItem: WarehouseItem | null;
}

const formSchema = z.object({
  organization_id: z.string().min(1, 'La organización es requerida'),
});

type FormData = z.infer<typeof formSchema>;

const EditWarehouseItemModal = ({
  isOpen,
  onClose,
  warehouseItem,
}: EditWarehouseItemModalProps) => {
  const { data: organizations = [] } = useOrganizations();
  const { mutate: updateItem, isPending } = useUpdateWarehouseItem();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      organization_id: '',
    },
  });

  useEffect(() => {
    if (warehouseItem && isOpen) {
      form.reset({
        organization_id: warehouseItem.organization_id || '',
      });
    }
  }, [warehouseItem, isOpen, form]);

  const handleSubmit = (data: FormData) => {
    if (!warehouseItem) return;

    updateItem(
      {
        id: warehouseItem.id,
        organization_id: data.organization_id,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Item del Almacén"
      description={warehouseItem?.product?.name || 'Producto'}
      form={form}
      onSubmit={handleSubmit}
      submitLabel="Guardar"
      isLoading={isPending}
      maxWidth="max-w-[425px]"
    >
      <div className="space-y-4 py-4">
        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organización</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una organización" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default EditWarehouseItemModal;
